import { randomNumber } from 'src/app/@utils/random-number';

import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { AccordionClasses } from '@shared/accordion/accordion.config';
import { AccordionService } from '@shared/accordion/accordion.service';
import { calculateAdjustVelocity, calculateVelocity } from '@shared/accordion/accordion.helpers';

@Directive({
    selector: '[appAccordionDirective]',
    standalone: true
})
export class AccordionDirective implements OnInit, AfterViewInit, OnDestroy {
    private nativeElement: HTMLElement;
    private button: HTMLButtonElement | null;
    private content: HTMLDivElement | null;
    private contentScrollHeight: number;
    private currentHeight: number;
    private boundEventHandler: () => void;

    @Input()
    private exclusive = false;

    @Input()
    private animate = true;

    @Input()
    private adjustVelocity: boolean = false;

    @Input()
    public expanded = false;

    @Input()
    public id?: string;

    @Output()
    public expandChange = new EventEmitter<boolean>();

    constructor(private element: ElementRef, private accordionService: AccordionService, private ngZone: NgZone) {
        this.button = null;
        this.content = null;
        this.contentScrollHeight = 0;
        this.currentHeight = 0;
    }

    ngOnInit(): void {
        if (!this.id) {
            this.id = `accordion_${randomNumber(0, 100000)}`;
        }
    }

    public ngAfterViewInit(): void {
        this.nativeElement = this.element.nativeElement;
        this.button = this.nativeElement.querySelector(`.${AccordionClasses.Button}`) as HTMLButtonElement;
        this.content = this.nativeElement.querySelector(`.${AccordionClasses.Content}`) as HTMLDivElement;

        if (this.button && this.content) {
            const idString = `accordion${this.id}`;
            this.button.setAttribute('aria-controls', idString);
            this.content.id = idString;
            if (this.exclusive) {
                this.accordionService.subscribeToChanges(this.callback.bind(this));
            }
            this.initAccordion();
        }
    }

    public expand(): void {
        if (this.expanded) {
            return;
        } else {
            this.handleExpanded();
        }
    }

    public collapse(): void {
        if (!this.expanded) {
            return;
        } else {
            this.handleExpanded();
        }
    }

    public ngOnDestroy(): void {
        if (this.button) {
            this.button.removeEventListener('click', this.boundEventHandler);
        }
    }

    private callback(el: HTMLElement): void {
        if (el !== this.nativeElement && this.expanded) {
            this.handleExpanded();
        }
    }

    private initAccordion(): void {
        this.boundEventHandler = this.handleExpanded.bind(this, false);
        this.button?.addEventListener('click', this.boundEventHandler);
        if (this.expanded) {
            this.handleExpanded(true);
        }
    }

    private handleAnimation(): void {
        this.ngZone.runOutsideAngular(() => {
            if (!this.button || !this.content) {
                return;
            }
            if (
                (this.expanded && this.currentHeight < this.contentScrollHeight) ||
                (!this.expanded && this.currentHeight > 0)
            ) {
                this.currentHeight = this.adjustVelocity
                    ? calculateAdjustVelocity(this.currentHeight, this.contentScrollHeight, this.expanded)
                    : calculateVelocity(this.currentHeight, this.contentScrollHeight, this.expanded);
            } else {
                this.content.style.height = this.expanded ? 'auto' : '0px';
                this.button.removeAttribute('disabled');
                return;
            }

            this.content.style.height = `${this.currentHeight}px`;
            requestAnimationFrame(this.handleAnimation.bind(this));
        });
    }

    private handleExpanded(fromInit: boolean = false): void {
        if (!this.button || !this.content) {
            return;
        }
        this.contentScrollHeight = this.content.scrollHeight;
        if (!fromInit) {
            this.expanded = !this.expanded;
            this.button.classList.toggle(AccordionClasses.ButtonActiveModifier);
        }
        this.button.setAttribute('aria-expanded', this.expanded.toString());
        if (this.animate && !fromInit) {
            this.button.setAttribute('disabled', 'true');
            this.handleAnimation();
        } else {
            this.content.style.height = this.expanded ? 'auto' : '0px';
        }

        if (this.expanded && this.exclusive) {
            this.accordionService.setActiveAccordion(this.nativeElement);
        }
        this.expandChange.emit(this.expanded);
    }
}
